import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import RichTextEditor from '../components/RichTextEditor'
import { BookOpen, Plus, Search, Eye, Edit2, Trash2, X, ChevronRight, Tag, User, Clock, BookMarked, Paperclip, Download, FileText } from 'lucide-react'

type KBCategory = 'TROUBLESHOOTING' | 'SETUP_GUIDE' | 'FAQ' | 'POLICY' | 'OTHER'

const CATEGORY_LABELS: Record<KBCategory, string> = {
  TROUBLESHOOTING: 'วิธีแก้ไขปัญหา',
  SETUP_GUIDE:     'คู่มือการตั้งค่า',
  FAQ:             'คำถามที่พบบ่อย',
  POLICY:          'นโยบาย/กระบวนการ',
  OTHER:           'อื่นๆ',
}

const CATEGORY_COLORS: Record<KBCategory, string> = {
  TROUBLESHOOTING: 'bg-red-50 text-red-700 border-red-200',
  SETUP_GUIDE:     'bg-blue-50 text-blue-700 border-blue-200',
  FAQ:             'bg-green-50 text-green-700 border-green-200',
  POLICY:          'bg-purple-50 text-purple-700 border-purple-200',
  OTHER:           'bg-gray-50 text-gray-600 border-gray-200',
}

interface Attachment {
  id: string; filename: string; originalName: string; url: string; size: number; mimeType: string; createdAt: string
}

interface Article {
  id: string; title: string; content?: string; category: KBCategory
  tags: string | null; isPublished: boolean; viewCount: number
  authorName: string | null; createdAt: string; updatedAt: string
  company?: { id: string; name: string } | null
  attachments?: Attachment[]
}

const EMPTY_FORM = { title: '', content: '', category: 'OTHER' as KBCategory, tags: '', isPublished: true }

interface Props { isAdmin?: boolean }

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function KnowledgeBasePage({ isAdmin }: Props) {
  const qc = useQueryClient()
  const [search,     setSearch]     = useState('')
  const [catFilter,  setCatFilter]  = useState('')
  const [viewing,    setViewing]    = useState<Article | null>(null)
  const [editing,    setEditing]    = useState<Article | 'new' | null>(null)
  const [form,       setForm]       = useState(EMPTY_FORM)
  const [delConfirm, setDelConfirm] = useState<string | null>(null)
  const [lightbox,   setLightbox]   = useState<string | null>(null)
  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [editorAttachments, setEditorAttachments] = useState<Attachment[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [editorUploading, setEditorUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const editorFileRef = useRef<HTMLInputElement>(null)

  const { data: articles = [], isLoading } = useQuery<Article[]>({
    queryKey: ['knowledge', catFilter, search],
    queryFn: () => {
      const p = new URLSearchParams()
      if (catFilter) p.set('category', catFilter)
      if (search)    p.set('search', search)
      return api.get(`/knowledge${p.toString() ? `?${p}` : ''}`).then(r => r.data)
    },
  })

  // Unfiltered-by-category list, used only to compute the per-category counts
  // above the search box — so selecting a category doesn't zero out the rest.
  const { data: allArticles = [] } = useQuery<Article[]>({
    queryKey: ['knowledge-all', search],
    queryFn: () => {
      const p = new URLSearchParams()
      if (search) p.set('search', search)
      return api.get(`/knowledge${p.toString() ? `?${p}` : ''}`).then(r => r.data)
    },
  })

  const fetchArticle = async (id: string) => {
    const r = await api.get(`/knowledge/${id}`)
    setViewing(r.data)
  }

  const createMut = useMutation({
    mutationFn: (data: typeof EMPTY_FORM) => api.post('/knowledge', data),
    onSuccess: async (res) => {
      if (pendingFiles.length > 0) {
        const fd = new FormData()
        pendingFiles.forEach(f => fd.append('files', f))
        try {
          await api.post(`/knowledge/${res.data.id}/attachments`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        } catch { /* non-fatal */ }
        setPendingFiles([])
      }
      qc.invalidateQueries({ queryKey: ['knowledge'] })
      closeEditor()
    },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof EMPTY_FORM }) =>
      api.put(`/knowledge/${id}`, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
      if (viewing?.id === vars.id) fetchArticle(vars.id)
      closeEditor()
    },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/knowledge/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['knowledge'] }); setDelConfirm(null); setViewing(null) },
  })

  const deleteAttachmentMut = useMutation({
    mutationFn: ({ articleId, attachmentId }: { articleId: string; attachmentId: string }) =>
      api.delete(`/knowledge/${articleId}/attachments/${attachmentId}`),
    onSuccess: (_, vars) => {
      if (viewing?.id === vars.articleId) fetchArticle(vars.articleId)
      setEditorAttachments(prev => prev.filter(a => a.id !== vars.attachmentId))
    },
  })

  const handleEditorFileAdd = (files: FileList) => {
    setPendingFiles(prev => [...prev, ...Array.from(files)])
    if (editorFileRef.current) editorFileRef.current.value = ''
  }

  const handleEditorFileUpload = async (articleId: string, files: FileList) => {
    if (!files.length) return
    setEditorUploading(true)
    try {
      const fd = new FormData()
      Array.from(files).forEach(f => fd.append('files', f))
      const r = await api.post(`/knowledge/${articleId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setEditorAttachments(prev => [...prev, ...r.data])
    } catch (e: any) {
      alert(e.response?.data?.message || 'อัปโหลดไฟล์ไม่สำเร็จ')
    } finally {
      setEditorUploading(false)
      if (editorFileRef.current) editorFileRef.current.value = ''
    }
  }

  const handleFileUpload = async (articleId: string, files: FileList) => {
    if (!files.length) return
    setUploadingFiles(true)
    try {
      const fd = new FormData()
      Array.from(files).forEach(f => fd.append('files', f))
      await api.post(`/knowledge/${articleId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      await fetchArticle(articleId)
    } catch (e: any) {
      alert(e.response?.data?.message || 'อัปโหลดไฟล์ไม่สำเร็จ')
    } finally {
      setUploadingFiles(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const openNew = () => {
    setForm(EMPTY_FORM)
    setEditorAttachments([])
    setPendingFiles([])
    setEditing('new')
  }
  const openEdit = async (a: Article) => {
    setForm({ title: a.title, content: a.content || '', category: a.category, tags: a.tags || '', isPublished: a.isPublished })
    setPendingFiles([])
    setEditing(a)
    // fetch full article to get attachments
    try {
      const r = await api.get(`/knowledge/${a.id}`)
      setEditorAttachments(r.data.attachments ?? [])
    } catch {
      setEditorAttachments(a.attachments ?? [])
    }
  }
  const closeEditor = () => { setEditing(null); setEditorAttachments([]); setPendingFiles([]) }
  const handleSave = () => {
    if (editing === 'new') createMut.mutate(form)
    else if (editing) updateMut.mutate({ id: editing.id, data: form })
  }

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.tagName === 'IMG') setLightbox((target as HTMLImageElement).src)
  }

  const categoryCount = (cat: KBCategory) => allArticles.filter(a => a.category === cat).length

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen size={24} className="text-blue-600"/> Knowledge Base
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">คู่มือการตั้งค่าและวิธีแก้ไขปัญหา</p>
        </div>
        {isAdmin && (
          <button onClick={openNew}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus size={16}/> เพิ่มบทความ
          </button>
        )}
      </div>

      {/* Category Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {(Object.keys(CATEGORY_LABELS) as KBCategory[]).map(cat => (
          <button key={cat} onClick={() => setCatFilter(catFilter === cat ? '' : cat)}
            className={`p-3 rounded-xl border text-left transition-all ${catFilter === cat ? CATEGORY_COLORS[cat] + ' ring-2 ring-offset-1 ring-current' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
            <p className="text-xl font-bold text-gray-900">{categoryCount(cat)}</p>
            <p className="text-xs mt-0.5 text-gray-600">{CATEGORY_LABELS[cat]}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="ค้นหาบทความ, แท็ก..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        {(search || catFilter) && (
          <button onClick={() => { setSearch(''); setCatFilter('') }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={15}/>
          </button>
        )}
      </div>

      {/* Article List */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">กำลังโหลด...</div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <BookMarked size={40} className="mx-auto mb-3 opacity-30"/>
          <p>ยังไม่มีบทความ{catFilter || search ? 'ที่ตรงกัน' : ''}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {articles.map(a => (
            <div key={a.id}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group"
              onClick={() => fetchArticle(a.id)}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className={`text-xs px-2 py-0.5 rounded border font-medium ${CATEGORY_COLORS[a.category]}`}>
                  {CATEGORY_LABELS[a.category]}
                </span>
                {!a.isPublished && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">ฉบับร่าง</span>}
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-2">
                {a.title}
              </h3>
              {a.tags && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {a.tags.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                    <span key={t} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">#{t}</span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-gray-400 mt-auto">
                <span className="flex items-center gap-1"><Eye size={11}/> {a.viewCount}</span>
                <span className="flex items-center gap-1">
                  <Clock size={11}/>
                  {new Date(a.updatedAt).toLocaleDateString('th-TH', { day:'numeric', month:'short', year:'numeric' })}
                </span>
                <ChevronRight size={14} className="text-gray-300 group-hover:text-blue-400"/>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Article Viewer */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl my-6 shadow-2xl flex flex-col max-h-[94vh]">
            <div className="flex items-start justify-between p-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex-1 min-w-0 pr-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded border font-medium ${CATEGORY_COLORS[viewing.category]}`}>
                    {CATEGORY_LABELS[viewing.category]}
                  </span>
                  {!viewing.isPublished && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">ฉบับร่าง</span>}
                </div>
                <h2 className="text-xl font-bold text-gray-900">{viewing.title}</h2>
                <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-400">
                  {viewing.authorName && <span className="flex items-center gap-1"><User size={11}/> {viewing.authorName}</span>}
                  <span className="flex items-center gap-1"><Eye size={11}/> {viewing.viewCount} ครั้ง</span>
                  <span className="flex items-center gap-1">
                    <Clock size={11}/>
                    อัปเดต {new Date(viewing.updatedAt).toLocaleDateString('th-TH', { day:'numeric', month:'long', year:'numeric' })}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {isAdmin && (
                  <>
                    <button onClick={() => openEdit(viewing)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      <Edit2 size={15}/>
                    </button>
                    <button onClick={() => setDelConfirm(viewing.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 size={15}/>
                    </button>
                  </>
                )}
                <button onClick={() => setViewing(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <X size={16}/>
                </button>
              </div>
            </div>

            {viewing.tags && (
              <div className="px-5 pt-3 flex flex-wrap gap-1.5 flex-shrink-0">
                {viewing.tags.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                  <span key={t} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    <Tag size={10}/> {t}
                  </span>
                ))}
              </div>
            )}

            <div className="p-6 overflow-y-auto flex-1" onClick={handleContentClick}>
              {viewing.content ? (
                <div
                  className="kb-content"
                  dangerouslySetInnerHTML={{ __html: viewing.content }}
                />
              ) : (
                <p className="text-gray-400 italic text-sm">ยังไม่มีเนื้อหา</p>
              )}
            </div>

            {/* Attachments section */}
            <div className="px-6 pb-6 border-t border-gray-100 pt-4 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Paperclip size={14}/> ไฟล์แนบ
                  <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                    {viewing.attachments?.length ?? 0}
                  </span>
                </h3>
                {isAdmin && (
                  <label className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${uploadingFiles ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
                    <Plus size={12}/> {uploadingFiles ? 'กำลังอัปโหลด...' : 'แนบไฟล์'}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      disabled={uploadingFiles}
                      onChange={e => e.target.files && handleFileUpload(viewing.id, e.target.files)}
                    />
                  </label>
                )}
              </div>

              {(viewing.attachments?.length ?? 0) === 0 ? (
                <p className="text-xs text-gray-400 italic">ยังไม่มีไฟล์แนบ</p>
              ) : (
                <div className="space-y-1.5">
                  {viewing.attachments!.map(att => (
                    <div key={att.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group">
                      <FileText size={14} className="text-gray-400 flex-shrink-0"/>
                      <a
                        href={att.url}
                        download={att.originalName}
                        className="flex-1 min-w-0 text-sm text-blue-600 hover:underline truncate"
                        onClick={e => e.stopPropagation()}
                      >
                        {att.originalName}
                      </a>
                      <span className="text-xs text-gray-400 flex-shrink-0">{fmtSize(att.size)}</span>
                      <a
                        href={att.url}
                        download={att.originalName}
                        className="p-1 text-gray-400 hover:text-blue-600 flex-shrink-0"
                        onClick={e => e.stopPropagation()}
                        title="ดาวน์โหลด"
                      >
                        <Download size={13}/>
                      </a>
                      {isAdmin && (
                        <button
                          onClick={e => { e.stopPropagation(); deleteAttachmentMut.mutate({ articleId: viewing.id, attachmentId: att.id }) }}
                          className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="ลบไฟล์"
                        >
                          <X size={13}/>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[94vh]">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
              <h2 className="font-semibold text-gray-800">
                {editing === 'new' ? 'เพิ่มบทความใหม่' : 'แก้ไขบทความ'}
              </h2>
              <button onClick={closeEditor} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <X size={16}/>
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อบทความ *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="เช่น วิธีแก้ปัญหา Windows Update ติดค้าง"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">หมวดหมู่</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value as KBCategory }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {(Object.entries(CATEGORY_LABELS) as [KBCategory, string][]).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">แท็ก (คั่นด้วยจุลภาค)</label>
                  <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                    placeholder="เช่น windows, vpn, printer"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">เนื้อหา</label>
                <RichTextEditor
                  content={form.content}
                  onChange={html => setForm(f => ({ ...f, content: html }))}
                  placeholder="พิมพ์เนื้อหา วาง Ctrl+V รูปได้เลย หรือลากรูปมาวาง..."
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isPublished}
                  onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))}
                  className="w-4 h-4 rounded accent-blue-600"/>
                <span className="text-sm text-gray-700">เผยแพร่ (ผู้ใช้ทุกคนสามารถเห็นได้)</span>
              </label>

              {/* File attachments in editor */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                    <Paperclip size={14}/> ไฟล์แนบ
                  </label>
                  <label className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${editorUploading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
                    <Plus size={12}/> {editorUploading ? 'กำลังอัปโหลด...' : 'เลือกไฟล์'}
                    <input
                      ref={editorFileRef}
                      type="file"
                      multiple
                      className="hidden"
                      disabled={editorUploading}
                      onChange={e => {
                        if (!e.target.files) return
                        if (editing === 'new') handleEditorFileAdd(e.target.files)
                        else if (editing && editing !== 'new') handleEditorFileUpload(editing.id, e.target.files)
                      }}
                    />
                  </label>
                </div>

                {/* Existing attachments (edit mode) */}
                {editorAttachments.map(att => (
                  <div key={att.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg group">
                    <FileText size={13} className="text-gray-400 flex-shrink-0"/>
                    <span className="flex-1 text-sm text-gray-700 truncate">{att.originalName}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{fmtSize(att.size)}</span>
                    <button
                      type="button"
                      onClick={() => editing && editing !== 'new' && deleteAttachmentMut.mutate({ articleId: editing.id, attachmentId: att.id })}
                      className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={13}/>
                    </button>
                  </div>
                ))}

                {/* Pending files (new article mode) */}
                {pendingFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-blue-50 rounded-lg group">
                    <FileText size={13} className="text-blue-400 flex-shrink-0"/>
                    <span className="flex-1 text-sm text-blue-700 truncate">{f.name}</span>
                    <span className="text-xs text-blue-400 flex-shrink-0">{fmtSize(f.size)}</span>
                    <button
                      type="button"
                      onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                      className="p-1 text-blue-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={13}/>
                    </button>
                  </div>
                ))}

                {editorAttachments.length === 0 && pendingFiles.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-1">ยังไม่มีไฟล์แนบ — รองรับ Word, PDF, Excel และอื่นๆ</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 p-5 border-t border-gray-100 flex-shrink-0">
              <button onClick={closeEditor}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                ยกเลิก
              </button>
              <button onClick={handleSave}
                disabled={!form.title.trim() || createMut.isPending || updateMut.isPending}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-colors">
                {(createMut.isPending || updateMut.isPending) ? 'กำลังบันทึก...' : 'บันทึก'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {delConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-semibold text-gray-900 mb-2">ยืนยันการลบ</h3>
            <p className="text-sm text-gray-600 mb-5">ต้องการลบบทความนี้ใช่ไหม? ไม่สามารถกู้คืนได้</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDelConfirm(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">ยกเลิก</button>
              <button onClick={() => deleteMut.mutate(delConfirm)}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg">ลบ</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightbox(null)}>
          <img src={lightbox} className="max-w-full max-h-full rounded-lg object-contain shadow-2xl" alt=""/>
          <button className="absolute top-4 right-4 text-white/70 hover:text-white bg-black/40 rounded-full p-2">
            <X size={20}/>
          </button>
        </div>
      )}
    </div>
  )
}
